<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TeamCreateDialog from './TeamCreateDialog.vue';
import TeamManageDialog from './TeamManageDialog.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();

const loading = ref({});
const searchQuery = ref('');
const teamCreateDialogRef = ref(null);
const teamManageDialogRef = ref(null);

const teamsList = useMapGetter('teams/getTeams');

const filteredTeamsList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return teamsList.value;
  return picoSearch(teamsList.value, query, ['name', 'description']);
});

const uiFlags = computed(() => getters['teams/getUIFlags'].value);

function openCreateTeamModal() {
  teamCreateDialogRef.value?.open();
}

function openManageTeamModal(team) {
  teamManageDialogRef.value?.open(team);
}

function onTeamCreateDialogClosed() {
  if (route.query.create === '1') {
    const nextQuery = { ...route.query };
    delete nextQuery.create;
    router.replace({ query: nextQuery });
  }
}

watch(
  () => route.query.create,
  val => {
    if (val === '1') {
      openCreateTeamModal();
    }
  },
  { immediate: true }
);

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('TEAMS_SETTINGS.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('TEAMS_SETTINGS.HEADER')"
        :description="$t('TEAMS_SETTINGS.DESCRIPTION')"
        :link-text="$t('TEAMS_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('TEAMS_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="team_management"
      >
        <template v-if="teamsList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('TEAMS_SETTINGS.COUNT', { n: teamsList.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            v-if="isAdmin"
            :label="$t('TEAMS_SETTINGS.NEW_TEAM')"
            size="sm"
            @click="openCreateTeamModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="!teamsList.length && !searchQuery.trim()"
        class="flex flex-col items-center justify-center px-0 py-16 text-center"
      >
        <div
          class="mb-6 flex size-24 items-center justify-center rounded-full bg-n-alpha-2 outline outline-1 outline-n-weak -outline-offset-1"
        >
          <Icon icon="i-lucide-users-round" class="size-12 text-n-slate-11" />
        </div>
        <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
          {{ $t('TEAMS_SETTINGS.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="mb-8 max-w-md text-body-main text-n-slate-11">
          {{ $t('TEAMS_SETTINGS.EMPTY_STATE.DESCRIPTION') }}
        </p>
        <Button
          v-if="isAdmin"
          :label="$t('TEAMS_SETTINGS.EMPTY_STATE.BUTTON')"
          @click="openCreateTeamModal"
        />
      </div>

      <span
        v-else-if="!filteredTeamsList.length && searchQuery.trim()"
        class="flex flex-1 items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('TEAMS_SETTINGS.NO_RESULTS') }}
      </span>

      <div v-else class="flex flex-col gap-3 pt-4">
        <div
          v-for="team in filteredTeamsList"
          :key="team.id"
          class="flex flex-row items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-5 py-3"
        >
          <div class="flex min-w-0 flex-1 items-center gap-4">
            <div
              class="flex size-12 shrink-0 items-center justify-center rounded-full bg-n-blue-4/25 dark:bg-n-blue-4/20"
            >
              <Icon icon="i-lucide-users" class="size-6 text-n-blue-11" />
            </div>
            <div class="min-w-0 flex-1">
              <p
                class="mb-0 truncate text-base font-semibold leading-snug text-n-slate-12"
              >
                <span>{{ team.name }}</span>
                <span class="ml-2 font-normal text-sm text-n-slate-11">
                  {{ $t('TEAMS_SETTINGS.LIST.ID_SUFFIX', { id: team.id }) }}
                </span>
              </p>
            </div>
          </div>
          <div
            v-if="isAdmin"
            class="flex shrink-0 flex-wrap items-center justify-end gap-3 sm:gap-4"
          >
            <Button
              variant="link"
              ruby
              sm
              icon="i-lucide-trash-2"
              :label="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
              :is-loading="loading[team.id]"
              class="!px-1 !font-bold"
              @click="openDelete(team)"
            />
            <Button
              outline
              slate
              sm
              icon="i-lucide-settings"
              :label="$t('TEAMS_SETTINGS.LIST.MANAGE')"
              class="rounded-xl !font-bold"
              @click="openManageTeamModal(team)"
            />
          </div>
        </div>
      </div>
    </template>
    <TeamCreateDialog
      ref="teamCreateDialogRef"
      @close="onTeamCreateDialogClosed"
    />
    <TeamManageDialog ref="teamManageDialogRef" />
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
